import React, { useEffect, useState } from "react";
import {
  EditCatalogueModal,
  StyleStep1,
  StyleStep2,
  StyleStep3,
} from "../../pages";
import { MimaToastUtil } from "../../components";
import { FashCreativesController } from "../../controllers";
import constant from "../../utils/constant";
import {
  convertUrlsToFiles,
  handleImageUpload,
  removeEmptyKeys,
} from "../../utils/utils";
import { fashCreativeStore } from "../../stores";
import { validationSchema } from "./validationSchema";

const EditCatalogueModalContainer = ({ closeModal }) => {
  const [loading, setLoading] = useState(false);
  const [newCollections, setNewCollections] = useState([]);

  const [hashTags, setHashTags] = useState([]);
  const [filterQuery, setFilterQuery] = useState("");

  useEffect(() => {
    const fetchCollections = async () => {
      if (filterQuery) {
        setLoading(true);
        await Promise.all([
          FashCreativesController.getCollections(filterQuery),
        ]);
        setLoading(false);
      }
    };
    fetchCollections();
  }, [filterQuery]);

  const catalogue = fashCreativeStore.singleCatalogue;

  useEffect(() => {
    // setPrevImageAdded(catalogue?.imageUrls);
    const fetchCollections = async () => {
      setLoading(true);
      await FashCreativesController.getCollections(catalogue?.business?._id);
      setLoading(false);
    };
    if (catalogue?.business?._id) {
      fetchCollections();
    }
    if (catalogue?.style?.styleCollections?.length > 0) {
      setNewCollections(catalogue.style.styleCollections);
    }
  }, [catalogue]);

  const [currentStep, setCurrentStep] = useState(1);

  const onSubmit = async (values) => {
    if (currentStep < 3) {
      return setCurrentStep(currentStep + 1);
    }

    if (values.imageUrls?.length < 1) {
      return MimaToastUtil.error({
        message: "Kindly Add atleast 1 image ",
      });
    }

    setLoading(true);
    const imagesToUpload = values.imageUrls?.filter((x) => !x.originalUrl);
    const oldImageUrls = values.imageUrls
      ?.filter((x) => x.originalUrl)
      .map((z) => z.originalUrl);

    let newImageUrls = [];

    if (imagesToUpload.length > 0) {
      newImageUrls = await handleImageUpload(imagesToUpload);
      if (newImageUrls.length < 1 || newImageUrls === constant.Failed) {
        setLoading(false);
        return MimaToastUtil.error({
          message: "Image upload failed, try again",
        });
      }
    }

    const urls = [...oldImageUrls, ...newImageUrls];

    const unifiedCollections = [
      ...values.styleCollections,
      ...values.newCollections,
    ];

    const updatedVariations = await Promise.all(
      values.variations.map(
        async ({
          customColors,
          colorsAvailable,
          international,
          local,
          addCustomColor,
          isDigital,
          documentUrls,
          ...rest
        }) => {
          const customColorsObjects = customColors?.map((color) => ({
            color,
            type: "CUSTOM",
          }));

          let urls = [];

          if (documentUrls?.length > 0) {
            urls = await handleImageUpload(documentUrls);
          }

          return {
            ...rest,
            ...(values.sellAbroad && values.pricingModel !== "BESPOKE"
              ? { international }
              : {}),
            ...(values.pricingModel !== "BESPOKE" ? { local } : {}),
            ...(colorsAvailable?.length > 0 || customColorsObjects?.length > 0
              ? {
                  colorsAvailable: [
                    ...(colorsAvailable || []),
                    ...(customColorsObjects || []),
                  ].filter((item) => item && Object.keys(item).length > 0),
                }
              : {}),
            //isDigital, ----> Temporary removal of isDigital
            documentUrls: urls,
          };
        },
      ),
    );

    const prePayload = {
      ...values,
      variations: updatedVariations,
    };

    let typeUpdatedVariations;

    if (values.type === "PRODUCT") {
      delete prePayload.availableDays;
      delete prePayload.availableWindow;

      typeUpdatedVariations = prePayload.variations.map(
        ({ durationInMinutes, ...rest }) => rest,
      );
    }

    if (values.type === "SERVICE") {
      typeUpdatedVariations = [...prePayload.variations];
    }

    const payload = {
      ...prePayload,
      variations: typeUpdatedVariations,
      imageUrls: urls,
      styleCollections: unifiedCollections,
    };

    delete payload.createCollections;
    delete payload.hasVariations;
    delete payload.newCollections;
    delete payload.sellAbroad;
    delete payload.tradingName;
    delete payload.isMigration;

    if (!values.description) {
      delete payload.description;
    }

    removeEmptyKeys(payload);

    const { status, errorMessage } =
      await FashCreativesController.editCatalogue(payload, catalogue._id);

    if (status === constant.Success) {
      MimaToastUtil.success({ message: "Style successfully updated" });
      setLoading(false);
      return closeModal();
    } else {
      setLoading(false);
      MimaToastUtil.error({ message: errorMessage });
    }
  };

  const [mappedVariations, setMappedVariations] = useState([]);
  const [blobbedImages, setBlobbedImages] = useState([]);

  useEffect(() => {
    const remapVariations = async () => {
      if (!catalogue?.style?.variations?.length) return;

      const mapped = await Promise.all(
        catalogue.style.variations.map(
          async ({
            colorsAvailable,
            sellingPrices,
            _id,
            documentUrls,
            ...rest
          }) => {
            const primaryColors = (colorsAvailable || [])
              .filter((x) => x.type === "PRIMARY")
              .map(({ _id, ...noIdColor }) => ({ ...noIdColor }));

            const customColors = (colorsAvailable || [])
              .filter((x) => x.type === "CUSTOM")
              .map(({ color }) => color);

            const local = sellingPrices
              .filter((x) => x.type === "LOCAL")
              .map(({ _id, type, ...noIdLocal }) => ({ ...noIdLocal }))[0];

            const international = sellingPrices
              .filter((x) => x.type === "INTERNATIONAL")
              .map(
                ({
                  _id,
                  stripeWithMaterialId,
                  withoutMaterial,
                  type,
                  ...noIdInt
                }) => ({
                  ...noIdInt,
                }),
              )[0];

            let docs = [];
            if (documentUrls?.length > 0) {
              try {
                docs = await convertUrlsToFiles(
                  documentUrls.map((url, i) => ({
                    url,
                    fileName: `doc-${i}`,
                  })),
                );
              } catch (error) {
                console.error("Error converting URLs to files", error);
              }
            }

            return {
              ...rest,
              colorsAvailable: primaryColors,
              customColors,
              addCustomColor: customColors.length > 0,
              local,
              international,
              documentUrls: docs,
            };
          },
        ),
      );

      setMappedVariations(mapped);
    };

    remapVariations();
  }, [catalogue?.style?.variations]);

  useEffect(() => {
    const afterBlob = async () => {
      let images = [];
      const imageUrls = catalogue?.imageUrls;
      if (imageUrls?.length > 0) {
        try {
          images = await convertUrlsToFiles(
            imageUrls.map((url, i) => ({
              url,
              fileName: `Image-${i}`,
            })),
          );
          setBlobbedImages(images);
        } catch (error) {
          console.error("Error converting URLs to files", error);
        }
      }
    };

    afterBlob();
  }, [catalogue?.imageUrls]);

  const initialValues = {
    //step 1
    name: catalogue?.name || "",
    isMigration: catalogue?.business?._id ? true : false,
    designerName: catalogue?.designerName || "",
    business: catalogue?.business?._id || "",
    type: catalogue?.type,
    bestSelling: catalogue?.style?.bestSelling || false,
    topView: catalogue?.style?.topView || false,
    createCollections: false,
    styleCollections: catalogue?.style?.styleCollections || [],
    newCollections: [],
    description: catalogue?.description || "",
    gender: catalogue?.gender || "",

    //step 2
    pricingModel: catalogue?.pricingModel || "FIXED_PRICE",
    hasVariations: catalogue?.variations?.length > 1 ? true : false,
    variations: mappedVariations,
    sellAbroad: mappedVariations[0]?.international?.currencyCode ? true : false,

    //Step 3
    imageUrls: blobbedImages || [],
    availableWindow: catalogue?.availableWindow || {
      startHour: 0,
      endHour: 0,
    },
    availableDays: catalogue?.availableDays || [],
    daysRequiredForDelivery: catalogue?.daysRequiredForDelivery || {
      early: 1,
      late: 1,
    },
    hashTags: catalogue?.hashTags || [],
  };

  const renderStep = (
    currentStep,
    values,
    handleChange,
    handleBlur,
    errors,
    touched,
    setFieldValue,
  ) => {
    switch (currentStep) {
      case 1:
        return (
          <StyleStep1
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            setFilterQuery={setFilterQuery}
          />
        );
      case 2:
        return (
          <StyleStep2
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            mappedVariations={mappedVariations}
            // setIsDisabled={setIsDisabled}
          />
        );
      case 3:
        return (
          <StyleStep3
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            blobbedImages={blobbedImages}
          />
        );
      default:
        return (
          <StyleStep1
            values={values}
            handleChange={handleChange}
            handleBlur={handleBlur}
            errors={errors}
            touched={touched}
            setFieldValue={setFieldValue}
            setFilterQuery={setFilterQuery}
          />
        );
    }
  };

  return (
    <EditCatalogueModal
      closeModal={closeModal}
      loading={loading}
      onSubmit={onSubmit}
      validationSchema={validationSchema(currentStep)}
      hashTags={hashTags}
      setHashTags={setHashTags}
      newCollections={newCollections}
      setNewCollections={setNewCollections}
      setFilterQuery={setFilterQuery}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      initialValues={initialValues}
      renderStep={renderStep}
    />
  );
};

export default EditCatalogueModalContainer;
